// Deal Explorer (§6) — buyer scenario wizard on the public funnel chrome.
// Anonymous, pre-auth, CLIENT-SIDE math only (prequalSizing.ts mirrors of
// the C# AmortizationMath); nothing is persisted, no session. The one
// network call is the on-demand server-rendered scenario deal-sheet PDF
// (POST /prequalification/deal-sheet/preview). Program terms are read-only
// from PREQUAL_PROGRAM_TERMS; every figure is illustrative. The 1 November 2026
// terms (M39): a 10/20-year picker defaulting to 20-year (D5), the D4 payment
// block (Buyer Onboarding and Setup Fee in principal, Payment Administration Fee
// with each payment, closed-form balloon) in cents (ruling Q1), and the
// fee statements in the buyer's wording (C1–C4, D6). Layout + styling follow the
// §4 public-funnel design system.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { formatCompactCurrency, formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { PortalShell } from "../../components/PortalShell";
import { prequalificationApi } from "../../api/prequalification";
import { CurrencyField, parseMoney } from "./PrequalBits";
import {
  buildProgramSchedule,
  computeProgramDealScenario,
  PREQUAL_PROGRAM_TERMS,
  type ProgramDealScenarioResult,
  type ProgramScheduleRow,
} from "./prequalSizing";
import { FREE_CASH_FLOW_DEFINITION, PROGRAM_OVERLINE } from "./prequalContent";
import { usePartnerBrand, usePrequalBase } from "./usePartnerBrand";
import { PrequalBrandProvider } from "./PrequalBrandContext";
import { PartnerFallbackNotice, PartnerThemeProvider } from "./PartnerThemeProvider";
import {
  loadExplorerState,
  loadFunnelState,
  PREQUAL_BUYER_STORAGE_KEY,
  saveExplorerState,
} from "./prequalStorage";

const CARD_SHELL =
  "rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)]";
const OVERLINE_MUTED = "text-label uppercase tracking-widest sm:tracking-[0.24em] text-ink-mid";
const GOLD_GRADIENT = "bg-gradient-to-r from-gold-deep via-gold to-gold-highlight";

/** §6 standing caveat — rendered on the page under the outputs. */
export const EXPLORER_CAVEAT_LINE =
  "Illustrative — subject to verification, not a commitment to lend.";

/** A fraction as the program documents print a percentage: 0.0495 → "4.95%". */
const termPercent = (fraction: number): string =>
  `${Number((fraction * 100).toFixed(4))}%`;

/** The $1,000,000 illustration C1 quotes the setup fee on. */
const SETUP_FEE_ILLUSTRATION_FINANCED = 1_000_000;

/** C3 states the premium share in words; the wording holds only while the share
 *  is one half (pinned by test). */
const PREPAYMENT_SHARE_WORDS: Record<number, string> = { 0.5: "half" };

/** The buyer's fee statements (C1–C4), verbatim from the program documents — the
 *  Explorer is buyer-facing (D6), so it states the buyer's own fees and alludes
 *  to the seller's only with C4. Every figure is formatted from
 *  `PREQUAL_PROGRAM_TERMS` (D1), so a term change moves this note with the
 *  constants the parity fixture pins. */
export const EXPLORER_FEE_NOTE =
  `The Buyer Onboarding and Setup Fee, ${termPercent(PREQUAL_PROGRAM_TERMS.buyerOnboardingSetupFeePct)} of the amount financed (${formatCurrency(SETUP_FEE_ILLUSTRATION_FINANCED * PREQUAL_PROGRAM_TERMS.buyerOnboardingSetupFeePct)} on ${formatCurrency(SETUP_FEE_ILLUSTRATION_FINANCED)} financed), is added to the principal of the note at closing, so nothing is payable upfront. It bears interest at the note rate and is a finance charge. `
  + `A Payment Administration Fee of ${termPercent(PREQUAL_PROGRAM_TERMS.buyerPaymentAdminFeePct)} of each scheduled monthly payment is payable in addition to that payment and debited with it, and ends at payoff or acceleration. `
  + `An early payoff carries a prepayment premium of ${PREPAYMENT_SHARE_WORDS[PREQUAL_PROGRAM_TERMS.prepaymentPremiumInterestShare]} the remaining scheduled interest through month ${PREQUAL_PROGRAM_TERMS.prepaymentPremiumThroughMonth}; after that, none. `
  + "Phoenix is also paid by the seller under the seller's own agreements; those fees are not added to your note.";

/** What the page shows in place of its figures when the down payment is zero or
 *  blank (approval §2): Phoenix sets no minimum, and a deal with no down payment
 *  is a conversation with the broker and a decision for the note holder. */
export const EXPLORER_NO_DOWN_PAYMENT_MESSAGE =
  "Enter a down payment to model this deal. For a deal with no down payment, talk with your broker; the note holder decides the down payment.";

/** The down-payment hint's tail (approval §2). */
const DOWN_PAYMENT_HINT_TAIL =
  "This is an indicative range note holders typically require. The down payment is negotiated with and decided by the note holder; Phoenix sets no minimum.";

const MAX_PINNED = 2;

/** The schedules the picker offers, 20-year first: it is the default (D5). */
const SCHEDULE_OPTIONS = [
  PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear,
  PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear,
] as const;

/** Money to the cent — the Explorer is a screen (ruling Q1). */
function cents(amount: number | null | undefined): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface PinnedScenario {
  purchasePrice: number;
  downPayment: number;
  annualCashFlow: number;
  amortizationMonths: number;
  result: ProgramDealScenarioResult;
}

/** Defensive restore of the stored pinned scenarios — anything not
 *  scenario-shaped is dropped. */
function restorePinned(raw: unknown[]): PinnedScenario[] {
  return raw
    .filter((p): p is PinnedScenario => {
      if (typeof p !== "object" || p === null) return false;
      const s = p as Record<string, unknown>;
      return (
        typeof s.purchasePrice === "number" &&
        typeof s.downPayment === "number" &&
        typeof s.annualCashFlow === "number" &&
        typeof s.amortizationMonths === "number" &&
        typeof s.result === "object" &&
        s.result !== null &&
        // A scenario pinned before the 1 November 2026 terms carries no payment
        // block; it is dropped rather than rendered on the old basis.
        typeof (s.result as Record<string, unknown>).payments === "object"
      );
    })
    .slice(0, MAX_PINNED);
}

/** §7 — the Explorer round-trip breadcrumb target: finalized buyer state →
 *  the Result route's documents view; in-progress state → the buyer
 *  funnel; nothing stored → the role fork. Built relative to the current
 *  funnel base (native `/prequalify` or partner `/prequalify/{slug}`).
 *
 *  `homeHref` is the ESCAPE for a host that mounts this page without the
 *  funnel behind it — see {@link DealExplorerPageProps.homeHref}. The
 *  finalized branch is deliberately NOT covered by it: the Result route is the
 *  one in-funnel destination that exists in both namespaces, so a host with no
 *  funnel can still send an operator back to the documents the application just
 *  produced, which is a better answer than its own index page. */
function resolveBreadcrumbTarget(base: string, homeHref?: string): string {
  const state = loadFunnelState<Record<string, unknown>>(PREQUAL_BUYER_STORAGE_KEY);
  if (state?.finalized) return `${base}/buyer/result?view=documents`;
  if (homeHref) return homeHref;
  if (state) return `${base}/buyer`;
  return base;
}

/** The scenario's coverage of twelve months' principal and interest on the Buyer
 *  Note Principal against the program's 1.50x sizing standard (T5(ii)). Not an
 *  approval test — a scenario below it is one the program would size smaller,
 *  so the "below" state is informational, not a rejection. */
function CoveragePill({ result }: { result: ProgramDealScenarioResult }) {
  if (result.sizingCoverage === null) return null;
  const standard = PREQUAL_PROGRAM_TERMS.sizingCoverage.toFixed(2);
  return (
    <span
      data-testid="explorer-dscr-pill"
      className={cn(
        "inline-flex items-center gap-space-1 rounded-pill border px-space-2 py-space-1 text-label whitespace-nowrap",
        result.meetsSizingStandard
          ? "border-sage text-sage bg-white-pure"
          : "border-gold-soft text-ink bg-gold-tint",
      )}
    >
      {result.meetsSizingStandard ? `≥ ${standard}x ✓` : `below ${standard}x`}
    </span>
  );
}

/** A returns fraction as "12.3%"; null (no down payment) renders an em dash. */
function pct(fraction: number | null): string {
  if (fraction === null) return "—";
  return `${(fraction * 100).toFixed(1)}%`;
}

function ScenarioRows({ result }: { result: ProgramDealScenarioResult }) {
  const p = result.payments;
  const rows: [string, string][] = [
    ["Amount financed", cents(result.amountFinanced)],
    [
      `Buyer Onboarding and Setup Fee (${(PREQUAL_PROGRAM_TERMS.buyerOnboardingSetupFeePct * 100).toFixed(2)}%)`,
      cents(p.setupFee),
    ],
    ["Buyer Note Principal", cents(p.buyerNotePrincipal)],
    ["Annual debt service", cents(result.annualDebtService)],
    ["Year-1 profit after debt service", cents(result.year1Profit)],
    ["Principal paid, year 1", cents(result.year1Principal)],
    ["Cash-on-cash return", pct(result.cashOnCash)],
    ["Total first-year return", pct(result.totalFirstYearReturn)],
    ["Estimated year-8 balloon", cents(p.balloon)],
  ];
  return (
    <dl className="m-0">
      {rows.map(([label, val], i) => (
        <div
          key={label}
          className={cn(
            "flex items-baseline justify-between gap-space-3 py-space-2",
            i > 0 && "border-t border-line",
          )}
        >
          {/* min-w-0 lets a long label ("Year-1 profit after debt service")
              wrap instead of pushing the figure off a phone-width card. */}
          <dt className="text-caption text-ink-mid min-w-0">{label}</dt>
          <dd className="font-serif font-bold text-small text-ink m-0 text-right">{val}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The full month-by-month schedule through maturity, collapsed by default.
 *
 * COLLAPSED deliberately: the page's job is a scan-first answer (payment,
 * coverage, year-one economics), and 96 rows opened by default would bury it.
 * But a buyer or broker who wants to reconcile the payment against their own
 * amortization needs the rows, and "run your own schedule" is exactly the
 * advice that produced a 57% mismatch when the reader amortized over 96 months
 * instead of the schedule the payment is sized on.
 *
 * The rows are computed on the UNROUNDED payment and displayed rounded (ruling
 * Q1), so the last row's balance is exactly the closed-form balloon the payment
 * card states: one figure, printed twice.
 */
function ScheduleDisclosure({
  rows,
  result,
}: {
  rows: ProgramScheduleRow[];
  result: ProgramDealScenarioResult;
}) {
  if (rows.length === 0) return null;
  const t = PREQUAL_PROGRAM_TERMS;
  const final = rows[rows.length - 1];
  const months = result.amortizationMonths;
  return (
    <details className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")} data-testid="explorer-schedule">
      <summary className="text-small text-ink cursor-pointer select-none marker:text-gold-deep">
        Show the full payment schedule ({rows.length} months, through maturity)
      </summary>
      <p className="text-caption text-ink-mid mt-space-3 mb-space-3" data-testid="explorer-schedule-basis">
        Every payment is the same {cents(result.payments.monthlyPrincipalAndInterest)} of principal
        and interest, plus a Payment Administration Fee of{" "}
        {cents(result.payments.monthlyPaymentAdministrationFee)}, calculated on a {months / 12}-year (
        {months}-month) amortization of the Buyer Note Principal. The note matures at month{" "}
        {t.balloonMonth}, when the remaining balance is due as a balloon.
      </p>
      {/* Wide content scrolls inside its own container — the page body never
          scrolls sideways (Table &amp; Tile Standard §12). */}
      <div className="overflow-x-auto max-h-[28rem] overflow-y-auto border border-line rounded-md">
        <table className="w-full border-collapse text-caption">
          <thead className="sticky top-0 bg-white-pure">
            <tr>
              {[
                "Month",
                "Principal & interest",
                "Payment Administration Fee",
                "Interest",
                "Principal",
                "Balance",
              ].map((h, i) => (
                <th
                  key={h}
                  className={cn(
                    "text-label uppercase tracking-wide text-ink-mid font-normal",
                    "px-space-2 py-space-2 border-b border-line whitespace-nowrap",
                    i === 0 ? "text-left" : "text-right",
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} className={r.month % 2 === 0 ? "bg-white-warm" : undefined}>
                <td className="px-space-2 py-space-1 text-ink-mid whitespace-nowrap">{r.month}</td>
                <td className="px-space-2 py-space-1 text-right font-serif text-ink whitespace-nowrap">
                  {cents(r.principalAndInterest)}
                </td>
                <td className="px-space-2 py-space-1 text-right font-serif text-ink whitespace-nowrap">
                  {cents(r.paymentAdministrationFee)}
                </td>
                <td className="px-space-2 py-space-1 text-right font-serif text-ink whitespace-nowrap">
                  {cents(r.interest)}
                </td>
                <td className="px-space-2 py-space-1 text-right font-serif text-ink whitespace-nowrap">
                  {cents(r.principal)}
                </td>
                <td className="px-space-2 py-space-1 text-right font-serif text-ink whitespace-nowrap">
                  {cents(r.balance)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-ink font-semibold">
              <td
                className="px-space-2 py-space-2 text-ink whitespace-nowrap"
                colSpan={5}
                data-testid="explorer-schedule-balloon-label"
              >
                Balloon — month {final.month}
              </td>
              <td
                className="px-space-2 py-space-2 text-right font-serif text-ink whitespace-nowrap"
                data-testid="explorer-schedule-balloon"
              >
                {cents(final.balance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** Read-only program terms, from the client mirror of the C# constants. */
function ProgramTermsCard() {
  const t = PREQUAL_PROGRAM_TERMS;
  const pctOf = (fraction: number) => `${+(fraction * 100).toFixed(2)}%`;
  const rows: [string, string][] = [
    ["Interest rate", `${pctOf(t.interestRate)} fixed — Current Program Rate`],
    [
      "Amortization",
      `${t.amortizationMonthsTenYear} or ${t.amortizationMonthsTwentyYear} months (${t.amortizationMonthsTenYear / 12} or ${t.amortizationMonthsTwentyYear / 12} yr)`,
    ],
    ["Balloon", `month ${t.balloonMonth} (year ${t.balloonMonth / 12})`],
    ["Buyer Onboarding and Setup Fee", `${pctOf(t.buyerOnboardingSetupFeePct)} of amount financed, added to principal`],
    ["Payment Administration Fee", `${pctOf(t.buyerPaymentAdminFeePct)} of each monthly payment`],
    [
      "Program range",
      `${formatCompactCurrency(t.minAmountFinanced)} – ${formatCompactCurrency(t.maxAmountFinanced)} financed`,
    ],
  ];
  return (
    <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")} data-testid="explorer-program-terms">
      <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-1")}>Program terms</p>
      <dl className="m-0">
        {rows.map(([label, val], i) => (
          <div
            key={label}
            className={cn(
              "flex items-baseline justify-between gap-space-3 py-space-2",
              i > 0 && "border-t border-line",
            )}
          >
            <dt className="text-caption text-ink-mid min-w-0">{label}</dt>
            <dd className="font-serif font-bold text-small text-ink m-0 text-right">{val}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Compact column used by the side-by-side scenario compare. */
function CompareColumn({
  title,
  scenario,
  onUnpin,
}: {
  title: string;
  scenario: PinnedScenario;
  onUnpin?: () => void;
}) {
  const r = scenario.result;
  const rows: [string, string][] = [
    ["Schedule", `${scenario.amortizationMonths / 12}-year`],
    ["Price", formatCurrency(scenario.purchasePrice)],
    ["Down", formatCurrency(scenario.downPayment)],
    ["Cash flow", formatCurrency(scenario.annualCashFlow)],
    ["Total monthly debit", cents(r.payments.monthlyTotal)],
    ["Coverage", r.sizingCoverage === null ? "—" : `${r.sizingCoverage.toFixed(2)}x`],
    ["Year-1 profit", cents(r.year1Profit)],
    ["Cash-on-cash", pct(r.cashOnCash)],
    ["Total 1st-yr return", pct(r.totalFirstYearReturn)],
    ["Year-8 balloon", cents(r.payments.balloon)],
  ];
  return (
    <div className="border border-line rounded-md p-space-3 bg-white-pure min-w-0">
      <div className="flex items-center justify-between gap-space-2 mb-space-2">
        <p className="text-label uppercase tracking-wide text-ink-mid m-0">{title}</p>
        {onUnpin && (
          <button
            type="button"
            onClick={onUnpin}
            className="bg-transparent border-0 p-0 text-caption text-ink-mid hover:text-gold-deep cursor-pointer"
          >
            Unpin
          </button>
        )}
      </div>
      {rows.map(([label, val]) => (
        <div key={label} className="flex items-baseline justify-between gap-space-2 py-space-1">
          <span className="text-caption text-ink-mid">{label}</span>
          <span className="font-serif text-caption text-ink text-right">{val}</span>
        </div>
      ))}
    </div>
  );
}

export interface DealExplorerPageProps {
  /**
   * Where "back" goes when the funnel is NOT behind this page.
   *
   * The public funnel mounts the buyer form and the role fork alongside this
   * route, so the breadcrumb can name them and it does — pass nothing and this
   * page behaves exactly as it always has.
   *
   * The STAFF assisted namespace (`/deal-flow/prequalify/…`) mounts the assist
   * console and the two result screens and NOTHING ELSE: its funnel is
   * addressed by draft id, so `{base}/buyer` and the role fork are routes that
   * do not exist there and the breadcrumb resolved to a 404. This names
   * somewhere that does.
   *
   * Deliberately a prop rather than a `deal-flow/prequalify/*` catch-all
   * redirect: a catch-all would have silently swallowed the very defect this
   * change exists to fix — an unregistered route in that namespace would have
   * looked like a working link that went somewhere odd, instead of a visible
   * 404 naming the missing route. A visible 404 is the diagnostic.
   */
  homeHref?: string;
}

/**
 * Resolve the partner site for the Explorer, then render it.
 *
 * ── WHY THIS WRAPPER EXISTS ──────────────────────────────────────────────
 *
 * The flow's brand provider is mounted as a LAYOUT route with no `source`, so
 * it resolves from the route `:slug`. The public funnel carries one
 * (`/prequalify/{slug}/explore`) and was always branded correctly. The STAFF
 * assisted namespace carries none — its routes are addressed by draft id — so
 * the layout resolved native and a buyer who had just walked a partner-branded
 * assisted application through to their documents landed on Phoenix chrome the
 * moment they clicked "Model a deal".
 *
 * Its two siblings already solve this the same way and this is deliberately a
 * copy of them rather than a second mechanism: the assisted surface sources the
 * brand from the loaded draft, and the result screen from the finalized result
 * (`PrequalResultPage.tsx`). This sources it from the SAME finalized result, in
 * the same tab, under the same storage key `resolveBreadcrumbTarget` already
 * reads a few lines below.
 *
 * The provider's tri-state `source` carries every case with no branching here:
 * a partner application passes its slug, a Phoenix-run one passes `null` (which
 * fetches nothing), and no finalized state at all — a direct visit to the
 * standalone Explorer, which is a first-class way to reach this page — passes
 * `undefined`, which defers to the route and so leaves the public funnel's
 * behaviour exactly as it was.
 *
 * Nesting is free where it agrees: a provider whose source names the slug an
 * ancestor is already resolving ADOPTS that answer instead of issuing a second
 * request, so the public funnel gains no round trip from this.
 */
export function DealExplorerPage({ homeHref }: DealExplorerPageProps = {}) {
  // Read once at mount, like the result screen. The shape guard drops a
  // stale/invalid blob, and `finalized` is null until an application completes.
  const [originating] = useState(() => {
    const stored = loadFunnelState<Record<string, unknown>>(PREQUAL_BUYER_STORAGE_KEY);
    const first = typeof stored?.form.firstName === "string" ? stored.form.firstName : "";
    const last = typeof stored?.form.lastName === "string" ? stored.form.lastName : "";
    return {
      finalized: stored?.finalized ?? null,
      // Composed here, from the form, because the server deliberately does not
      // read it off the submission — see the request DTO. Empty when the funnel
      // captured no name, which the sheet renders as its generic label.
      buyerFullName: [first, last].map((p) => p.trim()).filter(Boolean).join(" ") || undefined,
    };
  });

  return (
    <PrequalBrandProvider source={originating.finalized?.partnerSlug}>
      <DealExplorerBody
        homeHref={homeHref}
        submissionId={originating.finalized?.submissionId}
        buyerFullName={originating.buyerFullName}
      />
    </PrequalBrandProvider>
  );
}

function DealExplorerBody({
  homeHref,
  submissionId,
  buyerFullName,
}: DealExplorerPageProps & { submissionId?: string; buyerFullName?: string }) {
  const { brand, loading: brandPending, notFound } = usePartnerBrand();
  const base = usePrequalBase();
  // §7 — the scenario persists to its own sessionStorage key so the
  // prequal round-trip never loses it.
  const [restored] = useState(() => loadExplorerState());
  const [breadcrumbTarget] = useState(() => resolveBreadcrumbTarget(base, homeHref));
  // Grouped digit strings, like the funnel currency fields.
  const [purchasePrice, setPurchasePrice] = useState(restored?.purchasePrice ?? "");
  const [downPayment, setDownPayment] = useState(restored?.downPayment ?? "");
  const [annualCashFlow, setAnnualCashFlow] = useState(restored?.annualCashFlow ?? "");
  const [expectedPurchaseDate, setExpectedPurchaseDate] = useState(
    restored?.expectedPurchaseDate ?? "",
  );
  // The buyer's schedule election (D5): 20-year by default, including for a
  // scenario stored before the picker existed.
  const [amortizationMonths, setAmortizationMonths] = useState<number>(
    restored?.amortizationMonths ?? PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear,
  );
  const [pinned, setPinned] = useState<PinnedScenario[]>(() =>
    restored ? restorePinned(restored.pinned) : [],
  );
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  useEffect(() => {
    saveExplorerState({
      purchasePrice,
      downPayment,
      annualCashFlow,
      expectedPurchaseDate,
      amortizationMonths,
      pinned,
    });
  }, [purchasePrice, downPayment, annualCashFlow, expectedPurchaseDate, amortizationMonths, pinned]);

  const price = parseMoney(purchasePrice) ?? 0;
  const down = parseMoney(downPayment) ?? 0;
  const cashFlow = parseMoney(annualCashFlow) ?? 0;

  const figuresEntered = price > 0 && cashFlow > 0;
  // A zero or blank down payment is refused (approval §2): the page shows the
  // broker message in place of the figures rather than modelling a deal with no
  // down payment, which is a decision for the note holder.
  const noDownPayment = figuresEntered && down <= 0;
  const ready = figuresEntered && down > 0;
  const result = useMemo(
    () =>
      ready
        ? computeProgramDealScenario({
            purchasePrice: price,
            downPayment: down,
            annualCashFlow: cashFlow,
            amortizationMonths,
          })
        : null,
    [ready, price, down, cashFlow, amortizationMonths],
  );

  // Built from the SAME payment block the tile shows, so the schedule can never
  // describe a different loan than the figure above it.
  const schedule = useMemo(
    () => (result ? buildProgramSchedule(result.payments) : []),
    [result],
  );

  const downPct = price > 0 && down > 0 ? (down / price) * 100 : null;

  // §6 guardrails — soft warnings, never hard blocks. T1's range is measured on
  // the AMOUNT FINANCED, not the price.
  const amountFinanced = Math.max(0, price - down);
  const financedOutOfRange =
    price > 0 &&
    (amountFinanced < PREQUAL_PROGRAM_TERMS.minAmountFinanced ||
      amountFinanced > PREQUAL_PROGRAM_TERMS.maxAmountFinanced);
  const downOutOfShape = downPct !== null && (downPct < 4 || downPct > 20);

  function pinScenario() {
    if (!result || pinned.length >= MAX_PINNED) return;
    setPinned((p) => [
      ...p,
      { purchasePrice: price, downPayment: down, annualCashFlow: cashFlow, amortizationMonths, result },
    ]);
  }

  async function downloadDealSheet() {
    if (!result || downloading) return;
    setDownloadFailed(false);
    setDownloading(true);
    try {
      const { blob, fileName } = await prequalificationApi.dealSheetPreview({
        purchasePrice: price,
        downPayment: down,
        annualCashFlow: cashFlow,
        expectedPurchaseDate: expectedPurchaseDate || undefined,
        // The brand is derived from THIS, server-side; the name rides along
        // because the server deliberately reads nothing but the brand off the
        // record. Both absent on the standalone Explorer, which renders native.
        submissionId,
        buyerFullName,
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      // The server names the file — it follows the brand, and a blob URL
      // carries no Content-Disposition for the browser to honour on its own.
      anchor.download = fileName ?? "deal-sheet-scenario.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Quiet inline note — no toast infra in the portal.
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <PartnerThemeProvider brand={brand}>
    <PortalShell pageTitle="Deal explorer" wide publicFunnel partner={brand} brandPending={brandPending}>
      {notFound && <PartnerFallbackNotice />}
      <p className="text-small mt-0 mb-space-3">
        <Link
          to={breadcrumbTarget}
          data-testid="explorer-breadcrumb"
          className="text-ink-mid no-underline hover:text-gold-deep"
        >
          ← Back to your prequalification
        </Link>
      </p>
      <div className="mb-space-4">
        <p className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2">
          {PROGRAM_OVERLINE}
        </p>
        <h1 className="text-h3 sm:text-h2 font-display uppercase tracking-wide text-ink m-0">Deal explorer</h1>
        <div className="h-[3px] w-16 bg-gold mt-space-2" aria-hidden="true" />
        <p className="text-small text-ink-mid mt-space-3 mb-0">
          Model a purchase at program terms — change the price, down payment, and cash flow and
          watch the numbers update live.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-space-5">
        {/* ── Inputs ──────────────────────────────────────────────── */}
        <div className="flex flex-col gap-space-4">
          <section className={cn(CARD_SHELL, "p-space-4 sm:p-space-6")}>
            <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-3")}>Your scenario</p>
            <div className="flex flex-col gap-space-3">
              <CurrencyField
                label="Purchase price"
                required
                value={purchasePrice}
                onChange={setPurchasePrice}
                testId="explorer-price"
              />
              <CurrencyField
                label="Down payment"
                required
                value={downPayment}
                onChange={setDownPayment}
                // Approval §2 (2 November): the 5% planning midpoint, followed by
                // the note-holder sentence. Context ("how does mine compare"),
                // never a requirement — Phoenix sets no minimum.
                helperText={
                  downPct !== null
                    ? `≈ ${downPct.toFixed(1)}% of price · the program plans around ` +
                      `${PREQUAL_PROGRAM_TERMS.downPaymentMidpoint * 100}%. ${DOWN_PAYMENT_HINT_TAIL}`
                    : undefined
                }
                testId="explorer-down"
              />
              <CurrencyField
                label="Annual free cash flow"
                required
                value={annualCashFlow}
                onChange={setAnnualCashFlow}
                helperText={FREE_CASH_FLOW_DEFINITION}
                testId="explorer-cashflow"
              />
              <div data-testid="explorer-schedule-picker">
                <p className="text-small text-ink font-semibold mt-0 mb-space-2">Amortization schedule</p>
                <div
                  role="radiogroup"
                  aria-label="Amortization schedule"
                  className="inline-flex rounded-pill border border-line overflow-hidden"
                >
                  {SCHEDULE_OPTIONS.map((months) => {
                    const selected = amortizationMonths === months;
                    return (
                      <button
                        key={months}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setAmortizationMonths(months)}
                        data-testid={`explorer-schedule-${months}`}
                        className={cn(
                          "px-space-4 py-space-2 text-small border-0 cursor-pointer",
                          selected ? "bg-ink text-gold" : "bg-white-pure text-ink hover:bg-gold-tint",
                        )}
                      >
                        {months / 12}-year
                      </button>
                    );
                  })}
                </div>
                <p className="text-caption text-ink-mid mt-space-2 mb-0">
                  Elected by the buyer at closing and irrevocable. Both schedules have a balloon at
                  the end of Year {PREQUAL_PROGRAM_TERMS.balloonMonth / 12}.
                </p>
              </div>
              <FormField label="Expected purchase date" helperText="Optional.">
                <Input
                  type="date"
                  value={expectedPurchaseDate}
                  onChange={(e) => setExpectedPurchaseDate(e.target.value)}
                  data-testid="explorer-date"
                />
              </FormField>
            </div>
            {financedOutOfRange && (
              <p
                className="text-small text-ink bg-gold-tint border border-gold-soft rounded-md p-space-3 mt-space-3 mb-0"
                data-testid="explorer-price-warning"
              >
                The program finances {formatCurrency(PREQUAL_PROGRAM_TERMS.minAmountFinanced)} to $
                {PREQUAL_PROGRAM_TERMS.maxAmountFinanced / 1_000_000} million per acquisition. This
                scenario finances {formatCurrency(amountFinanced)}, outside that range. You can keep
                modeling; the figures stay illustrative.
              </p>
            )}
            {downOutOfShape && (
              <p
                className="text-small text-ink bg-gold-tint border border-gold-soft rounded-md p-space-3 mt-space-3 mb-0"
                data-testid="explorer-down-warning"
              >
                A down payment of {downPct?.toFixed(1)}% is outside the range note holders
                typically accept (roughly 4–20% of the price). The note holder decides.
              </p>
            )}
          </section>
          <ProgramTermsCard />
        </div>

        {/* ── Outputs ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-space-4">
          {result ? (
            <>
              <div className={cn(CARD_SHELL, "overflow-hidden")}>
                <div className={cn(GOLD_GRADIENT, "h-[3px]")} aria-hidden="true" />
                <div className="p-space-4 sm:p-space-5">
                  <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-2")}>Monthly payment</p>
                  <p
                    className="font-serif text-[1.75rem] sm:text-[2.25rem] leading-none text-ink m-0"
                    data-testid="explorer-monthly-payment"
                  >
                    {cents(result.payments.monthlyTotal)}
                  </p>
                  <p
                    className="text-caption text-ink-mid mt-space-2 mb-0"
                    data-testid="explorer-payment-breakdown"
                  >
                    Principal and interest {cents(result.payments.monthlyPrincipalAndInterest)} ·
                    Payment Administration Fee {cents(result.payments.monthlyPaymentAdministrationFee)}
                  </p>
                  {/* The payment's BASIS, at the payment: the Buyer Note Principal
                      (the amount financed plus the setup fee), the schedule it is
                      amortized over, and the balloon. Deliberately avoids the word
                      "term": the note matures at 96 months and the payment is sized
                      on the amortization, and that one word collapses the two. */}
                  <p
                    className="text-caption text-ink-mid mt-space-2 mb-0"
                    data-testid="explorer-payment-basis"
                  >
                    On a Buyer Note Principal of {cents(result.payments.buyerNotePrincipal)} (
                    {cents(result.amountFinanced)} financed plus the {cents(result.payments.setupFee)}{" "}
                    Buyer Onboarding and Setup Fee), amortized over {result.amortizationMonths / 12}{" "}
                    years ({result.amortizationMonths} months). The note matures at month{" "}
                    {PREQUAL_PROGRAM_TERMS.balloonMonth} (year {PREQUAL_PROGRAM_TERMS.balloonMonth / 12}
                    ), when the remaining balance of about {cents(result.payments.balloon)} is due as a
                    balloon.
                  </p>
                  <div className="flex items-center gap-space-2 mt-space-3">
                    <span className="text-caption text-ink-mid" data-testid="explorer-coverage">
                      Coverage{" "}
                      {result.sizingCoverage === null ? "—" : `${result.sizingCoverage.toFixed(2)}x`}
                    </span>
                    <CoveragePill result={result} />
                  </div>
                  <p
                    className="text-caption text-ink-mid mt-space-2 mb-0"
                    data-testid="explorer-maximum-financed"
                  >
                    Maximum Financed Amount on this schedule: {cents(result.maximumFinanced)}. This
                    scenario finances {cents(result.amountFinanced)},{" "}
                    {result.withinMaximum ? "within" : "above"} it.
                  </p>
                </div>
              </div>

              <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")}>
                <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-1")}>Year-one economics</p>
                <ScenarioRows result={result} />
              </div>

              <ScheduleDisclosure rows={schedule} result={result} />

              <div className="flex flex-wrap items-center gap-space-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={pinScenario}
                  disabled={pinned.length >= MAX_PINNED}
                  data-testid="explorer-pin"
                >
                  Pin this scenario
                </Button>
                <Button
                  type="button"
                  onClick={() => void downloadDealSheet()}
                  disabled={downloading}
                  className="hover:bg-gold-deep hover:text-white-pure"
                  data-testid="explorer-download"
                >
                  {downloading ? "Preparing…" : "Download deal sheet (PDF)"}
                </Button>
              </div>
              {downloadFailed && (
                <p className="text-small text-ink-mid m-0" data-testid="explorer-download-error">
                  Preview unavailable right now
                </p>
              )}
            </>
          ) : noDownPayment ? (
            <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")}>
              <p className="text-small text-ink m-0" data-testid="explorer-no-down-payment">
                {EXPLORER_NO_DOWN_PAYMENT_MESSAGE}
              </p>
            </div>
          ) : (
            <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")}>
              <p className="text-small text-ink-mid m-0">
                Enter a purchase price and annual cash flow to see the numbers — they update as
                you type.
              </p>
            </div>
          )}

          {pinned.length > 0 && (
            <div className={cn(CARD_SHELL, "p-space-4 sm:p-space-5")} data-testid="explorer-compare">
              <p className={cn(OVERLINE_MUTED, "mt-0 mb-space-3")}>Compare scenarios</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-3">
                {pinned.map((scenario, idx) => (
                  <CompareColumn
                    key={idx}
                    title={`Pinned ${idx + 1}`}
                    scenario={scenario}
                    onUnpin={() => setPinned((p) => p.filter((_, i) => i !== idx))}
                  />
                ))}
                {result && (
                  <CompareColumn
                    title="Live"
                    scenario={{
                      purchasePrice: price,
                      downPayment: down,
                      annualCashFlow: cashFlow,
                      amortizationMonths,
                      result,
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {result && (
            <p className="text-caption text-ink-mid m-0" data-testid="explorer-fee-note">
              {EXPLORER_FEE_NOTE}
            </p>
          )}
          <p className="text-caption text-ink-faint m-0">{EXPLORER_CAVEAT_LINE}</p>
        </div>
      </div>
    </PortalShell>
    </PartnerThemeProvider>
  );
}
